import "./env-module.css";

import { CurveEditor, type CurvePoint } from "./curve-editor.ts";
import { Knob } from "./knob.ts";

// EnvModule — ADSR envelope editor with a 3-handle visual + 4 small knobs
// for direct numeric edit. Two-way sync: dragging a handle updates the
// matching knob; rotating a knob moves the handle.
//
// Engine envelopes are linear-segment ADSRs, so the editor uses straight
// segments (no curvature mids — adding them would suggest a shape
// parameter the engine doesn't have).
//
// The editor's X axis is a log-scaled time axis from 0 to ~20s, so a 1 ms
// attack and a 5 s attack are both visible as meaningful segment widths.

export interface EnvState {
  attackMs: number;
  decayMs: number;
  sustain: number;
  releaseMs: number;
}

export interface EnvModuleOptions {
  container: HTMLElement;
  /** Display title (e.g. "amp env" / "filter env"). */
  title: string;
  /** Engine param path prefix (e.g. "amp_env" / "filter_env"). The module
   *  fires `${pathBase}.attack_ms` etc. */
  pathBase: string;
  setParam: (path: string, value: number) => void;
  initial?: Partial<EnvState>;
}

export const MIN_ATK_MS = 0.1;
export const MAX_ATK_MS = 5000;
export const MIN_DEC_MS = 1;
export const MAX_DEC_MS = 5000;
export const MIN_REL_MS = 1;
export const MAX_REL_MS = 8000;
export const MAX_TOTAL_MS = MAX_ATK_MS + MAX_DEC_MS + MAX_REL_MS;
const LOG_TIME_MAX = Math.log(MAX_TOTAL_MS + 1);

export function timeToX(ms: number): number {
  return Math.log(Math.max(0, ms) + 1) / LOG_TIME_MAX;
}
export function xToTime(x: number): number {
  return Math.exp(Math.max(0, Math.min(1, x)) * LOG_TIME_MAX) - 1;
}

export function formatMs(v: number): string {
  if (v < 10) return `${v.toFixed(1)} ms`;
  if (v < 1000) return `${v.toFixed(0)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}

export class EnvModule {
  private root: HTMLDivElement;
  private editor: CurveEditor;
  private attackKnob: Knob;
  private decayKnob: Knob;
  private sustainKnob: Knob;
  private releaseKnob: Knob;

  private attackMs: number;
  private decayMs: number;
  private sustain: number;
  private releaseMs: number;
  private setParam: EnvModuleOptions["setParam"];
  private pathBase: string;

  constructor(opts: EnvModuleOptions) {
    const init = opts.initial ?? {};
    this.attackMs = init.attackMs ?? 5;
    this.decayMs = init.decayMs ?? 120;
    this.sustain = init.sustain ?? 0.6;
    this.releaseMs = init.releaseMs ?? 200;
    this.setParam = opts.setParam;
    this.pathBase = opts.pathBase;

    this.root = document.createElement("div");
    this.root.className = "env-module";

    // Header.
    const header = document.createElement("div");
    header.className = "env-module-header";
    const title = document.createElement("span");
    title.className = "env-module-title";
    title.textContent = opts.title;
    header.append(title);
    this.root.append(header);

    // Editor — 3 handles, no curve mids.
    const editorHost = document.createElement("div");
    editorHost.className = "env-module-editor";
    this.root.append(editorHost);

    const pts = this.computePoints();
    this.editor = new CurveEditor({
      container: editorHost,
      width: 360,
      height: 120,
      fluid: true,
      points: pts,
      draw: (ctx, w, h, p) => this.drawEnv(ctx, w, h, p),
      onChange: (p) => this.onHandleDrag(p),
    });

    // Knob row — atk / dec / sus / rel.
    const knobs = document.createElement("div");
    knobs.className = "env-module-knobs";
    this.root.append(knobs);

    this.attackKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_ATK_MS,
      max: MAX_ATK_MS,
      default: 5,
      value: this.attackMs,
      taper: "log",
      label: "atk",
      format: formatMs,
      onChange: (v) => {
        this.attackMs = v;
        this.pushEngine("attack_ms", v);
        this.syncEditorFromState();
      },
    });

    this.decayKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_DEC_MS,
      max: MAX_DEC_MS,
      default: 120,
      value: this.decayMs,
      taper: "log",
      label: "dec",
      format: formatMs,
      onChange: (v) => {
        this.decayMs = v;
        this.pushEngine("decay_ms", v);
        this.syncEditorFromState();
      },
    });

    this.sustainKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: 0.6,
      value: this.sustain,
      label: "sus",
      format: (v) => v.toFixed(2),
      onChange: (v) => {
        this.sustain = v;
        this.pushEngine("sustain", v);
        this.syncEditorFromState();
      },
    });

    this.releaseKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_REL_MS,
      max: MAX_REL_MS,
      default: 200,
      value: this.releaseMs,
      taper: "log",
      label: "rel",
      format: formatMs,
      onChange: (v) => {
        this.releaseMs = v;
        this.pushEngine("release_ms", v);
        this.syncEditorFromState();
      },
    });

    opts.container.append(this.root);
  }

  getState(): EnvState {
    return {
      attackMs: this.attackMs,
      decayMs: this.decayMs,
      sustain: this.sustain,
      releaseMs: this.releaseMs,
    };
  }

  setState(s: EnvState): void {
    // setValue on each knob fires onChange → pushEngine → engine, and
    // also calls syncEditorFromState so the ADSR editor's handles
    // update in lockstep. No further work needed.
    this.attackKnob.setValue(s.attackMs);
    this.decayKnob.setValue(s.decayMs);
    this.sustainKnob.setValue(s.sustain);
    this.releaseKnob.setValue(s.releaseMs);
  }

  destroy(): void {
    this.root.remove();
  }

  /** Build the three CurvePoint handles from the current ADSR state.
   *  Layout: [attack, decay/sustain, release]. The envelope is rendered
   *  as straight segments — engine envelopes are linear, so we don't
   *  add curvature mid handles that would lie about the audio path. */
  private computePoints(): CurvePoint[] {
    const ax = timeToX(this.attackMs);
    const dx = timeToX(this.attackMs + this.decayMs);
    const rx = timeToX(this.attackMs + this.decayMs + this.releaseMs);
    return [
      {
        x: ax,
        y: 1.0,
        drag: "x",
        xRange: [timeToX(MIN_ATK_MS), timeToX(MAX_ATK_MS)],
        yRange: [1, 1],
        lockNeighborsX: true,
        label: "attack",
      },
      {
        x: dx,
        y: this.sustain,
        drag: "xy",
        xRange: [
          timeToX(MIN_ATK_MS + MIN_DEC_MS),
          timeToX(MAX_ATK_MS + MAX_DEC_MS),
        ],
        yRange: [0, 1],
        lockNeighborsX: true,
        label: "decay/sustain",
      },
      {
        x: rx,
        y: 0,
        drag: "x",
        xRange: [
          timeToX(MIN_ATK_MS + MIN_DEC_MS + MIN_REL_MS),
          timeToX(MAX_TOTAL_MS),
        ],
        yRange: [0, 0],
        lockNeighborsX: true,
        label: "release",
      },
    ];
  }

  /** Recompute handle positions after a knob change. Mutates the editor's
   *  point array in place so the next draw picks up the change without an
   *  onChange round-trip. */
  private syncEditorFromState(): void {
    const p = this.editor.getPoints();
    if (p.length < 3) return;
    p[0]!.x = timeToX(this.attackMs);
    p[1]!.x = timeToX(this.attackMs + this.decayMs);
    p[1]!.y = this.sustain;
    p[2]!.x = timeToX(this.attackMs + this.decayMs + this.releaseMs);
    this.editor.setPoints(p);
  }

  /** Translate a handle drag into ADSR state updates and engine params. */
  private onHandleDrag(p: CurvePoint[]): void {
    const attack = p[0]!;
    const decay = p[1]!;
    const release = p[2]!;
    const newAttack = clamp(xToTime(attack.x), MIN_ATK_MS, MAX_ATK_MS);
    const tDecayEnd = xToTime(decay.x);
    const newDecay = clamp(tDecayEnd - newAttack, MIN_DEC_MS, MAX_DEC_MS);
    const tReleaseEnd = xToTime(release.x);
    const newRelease = clamp(
      tReleaseEnd - newAttack - newDecay,
      MIN_REL_MS,
      MAX_REL_MS,
    );
    const newSustain = clamp(decay.y, 0, 1);

    if (Math.abs(newAttack - this.attackMs) > 1e-3) {
      this.attackMs = newAttack;
      this.attackKnob.setValue(newAttack);
      this.pushEngine("attack_ms", newAttack);
    }
    if (Math.abs(newDecay - this.decayMs) > 1e-3) {
      this.decayMs = newDecay;
      this.decayKnob.setValue(newDecay);
      this.pushEngine("decay_ms", newDecay);
    }
    if (Math.abs(newRelease - this.releaseMs) > 1e-3) {
      this.releaseMs = newRelease;
      this.releaseKnob.setValue(newRelease);
      this.pushEngine("release_ms", newRelease);
    }
    if (Math.abs(newSustain - this.sustain) > 1e-3) {
      this.sustain = newSustain;
      this.sustainKnob.setValue(newSustain);
      this.pushEngine("sustain", newSustain);
    }
  }

  private pushEngine(suffix: string, value: number): void {
    this.setParam(`${this.pathBase}.${suffix}`, value);
  }

  /** Draw the envelope shape as three straight segments through the
   *  current handle positions. (0, 0) → attack → decay/sustain → release. */
  private drawEnv(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    pts: CurvePoint[],
  ): void {
    const attack = pts[0]!;
    const decay = pts[1]!;
    const release = pts[2]!;
    ctx.save();
    ctx.shadowColor = "rgba(56, 189, 248, 0.55)";
    ctx.shadowBlur = 12;
    ctx.strokeStyle = "rgba(125, 211, 252, 0.9)";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(0, h);
    ctx.lineTo(attack.x * w, (1 - attack.y) * h);
    ctx.lineTo(decay.x * w, (1 - decay.y) * h);
    ctx.lineTo(release.x * w, (1 - release.y) * h);
    ctx.stroke();
    ctx.restore();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
